import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { clear } = useCart();
  const { t } = useLang();
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [orderId, setOrderId] = useState(null);
  const attemptRef = useRef(0);
  const cartCleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;

    const poll = async () => {
      attemptRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") {
          if (!cartCleared.current) { clear(); cartCleared.current = true; }
          if (!cancelled) setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          if (!cancelled) setState("failed");
          return;
        }
      } catch (e) {
        console.warn("Payment status poll failed, will retry:", e);
      }
      if (attemptRef.current >= 10) {
        if (!cancelled) setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <main data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-24">
      <div className="bg-white rounded-3xl border border-zinc-100 p-10 text-center">
        {state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-brand mx-auto animate-spin" />
            <h1 className="mt-6 font-display font-extrabold text-2xl text-zinc-900">{t("payment.confirming")}</h1>
            <p className="mt-2 text-sm text-zinc-500">{t("payment.confirmingSub")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="mt-6 font-display font-extrabold text-3xl text-zinc-900">{t("payment.success")}</h1>
            <p className="mt-3 text-zinc-600">{t("payment.successMsg")}</p>
            {orderId && <div className="mt-4 font-mono text-xs text-zinc-500">{t("account.order")} {orderId}</div>}
            <div className="mt-8 flex gap-3 justify-center">
              <Button data-testid="ps-view-orders" onClick={() => navigate("/account")} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">{t("payment.viewOrders")}</Button>
              <Button data-testid="ps-shop-more" variant="outline" onClick={() => navigate("/shop")} className="rounded-full">{t("payment.keepShopping")}</Button>
            </div>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="w-12 h-12 text-zinc-400 mx-auto" />
            <h1 className="mt-6 font-display font-extrabold text-2xl text-zinc-900">{t("payment.stillProcessing")}</h1>
            <p className="mt-3 text-sm text-zinc-500">{t("payment.stillProcessingSub")}</p>
            <Button onClick={() => navigate("/account")} className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white">{t("payment.goToAccount")}</Button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-9 h-9 text-brand" />
            </div>
            <h1 className="mt-6 font-display font-extrabold text-2xl text-zinc-900">{t("payment.failed")}</h1>
            <p className="mt-3 text-sm text-zinc-500">{t("payment.failedSub")}</p>
            <Link to="/checkout"><Button className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white">{t("payment.backToCheckout")}</Button></Link>
          </>
        )}
      </div>
    </main>
  );
}
